import moment from 'moment';
import {
  ActionButton,
  Buttons,
  CancelButton,
  DayPickerInput,
  Drawer,
  HelpTooltip,
  SingleSelect,
  Table,
  TextInput,
} from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageLoader } from '~/routes/public/pages';
import styled from 'styled-components';
import { dateFormats } from '~/utils';
import OptionSelect from './OptionSelect';

const CustomContainer = styled.div`
  width: 100%;
  input {
    font-size: 0.875rem;
  }
`;

const DatePickerContainer = styled.div`
  width: 100%;
`;

export default function ConfigurationDrawer({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [configuration, setConfiguration] = useState({ data: null, isReady: false });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getConfiguration();
        if (!isMounted.current) return;
        setConfiguration({ data, isReady: true });
      } catch ({ message }) {
        if (!isMounted.current) return;
        toast.error(message ?? 'Failed to load Sage Intacct configuration.');
        setConfiguration({ data: null, isReady: true });
      }
    })();
  }, [api, workspace.id, isMounted, sageConnectionId, toast]);

  const [items, setItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getItems();
      if (!isMounted.current) return;
      setItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const [expenseAccounts, setExpenseAccounts] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getExpenseAccounts();
      if (!isMounted.current) return;
      setExpenseAccounts({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const [departments, setDepartments] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getDepartments();
      if (!isMounted.current) return;
      setDepartments({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const [invoiceItems, setInvoiceItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).invoiceItems().get();
      if (!isMounted.current) return;
      setInvoiceItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted]);

  const [paymentMethodValues, setPaymentMethodValues] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getPaymentMethodValues();
      if (!isMounted.current) return;
      setPaymentMethodValues({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const [orderEntryItems, setOrderEntryItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getOrderEntryItems();
      if (!isMounted.current) return;
      setOrderEntryItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const [purchasingItems, setPurchasingItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getPurchasingItems();
      if (!isMounted.current) return;
      setPurchasingItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  // VAT requires a tax detail on every invoice line; other solutions don't.
  const [taxDetails, setTaxDetails] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      if (!configuration.isReady) return;
      if (configuration.data?.sageTaxCalculationMethod !== 'VAT') {
        setTaxDetails({ data: [], isReady: true });
        return;
      }
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getTaxDetails();
      if (!isMounted.current) return;
      setTaxDetails({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId, configuration]);

  return (
    <Drawer
      isOpen
      title="Configuration"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          if (configuration.data.vendorBillEnabled && !configuration.data.vendorBillSyncStartDate) {
            toast.error('A sync start date is required when reimbursable expense sync is enabled.');
            return;
          }

          form.submit();
          try {
            await api.www
              .workspaces(workspace.id)
              .sage(sageConnectionId)
              .updateConfiguration({
                name: configuration.data.name,
                syncMethod: configuration.data.syncMethod ?? 'direct',
                defaultInvoiceItemId: configuration.data.defaultInvoiceItemId,
                sageDepartmentId: configuration.data.sageDepartmentId,
                sageIncomeAccountId: configuration.data.sageIncomeAccountId,
                sageExpenseAccountId: configuration.data.sageExpenseAccountId,
                orderEntryDefaultItemKey:
                  configuration.data.syncMethod === 'documents' ? configuration.data.orderEntryDefaultItemKey : null,
                purchasingDefaultItemKey:
                  configuration.data.syncMethod === 'documents' ? configuration.data.purchasingDefaultItemKey : null,
                sagePaymentMethodValue: configuration.data.sagePaymentMethodValue,
                sageTaxableTaxDetailId: configuration.data.sageTaxableTaxDetailId ?? null,
                sageNonTaxableTaxDetailId: configuration.data.sageNonTaxableTaxDetailId ?? null,
                sageNonTaxablePurchasingTaxDetailId: configuration.data.sageNonTaxablePurchasingTaxDetailId ?? null,
                defaultRemoveFromAccounting: configuration.data.defaultRemoveFromAccounting,
                vendorBillEnabled: !!configuration.data.vendorBillEnabled,
                vendorBillSyncStartDate: configuration.data.vendorBillSyncStartDate
                  ? moment(configuration.data.vendorBillSyncStartDate).format(dateFormats.isoDate)
                  : null,
              });
            form.save();
            if (isMounted.current) setDirty(false);
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        const handleChange = (event) => {
          setDirty(true);
          setConfiguration((state) => ({
            ...state,
            data: { ...state.data, [event.target.name]: event.target.value || null },
          }));
        };

        const handleValueChange = (key, value) => {
          setDirty(true);
          setConfiguration((state) => ({
            ...state,
            data: { ...state.data, [key]: value },
          }));
        };

        if (
          !configuration.isReady ||
          !items.isReady ||
          !expenseAccounts.isReady ||
          !departments.isReady ||
          !invoiceItems.isReady ||
          !paymentMethodValues.isReady ||
          !orderEntryItems.isReady ||
          !purchasingItems.isReady ||
          !taxDetails.isReady
        )
          return <PageLoader />;

        if (!configuration.data) {
          return (
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
              </Buttons>
            </Drawer.Actions>
          );
        }

        const isDocuments = configuration.data.syncMethod === 'documents';

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Setting</Table.Column>
                <Table.Column>
                  <strong>Value</strong>
                </Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                <Table.BoxRow>
                  <Table.Cell>
                    Integration Name
                    <HelpTooltip
                      message="The name of the Sage Intacct integration to be referenced in Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <TextInput
                      container={CustomContainer}
                      name="name"
                      value={configuration.data.name ?? ''}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Ruddr Invoice Item
                    <HelpTooltip
                      message="Ruddr will use this default Invoice Item when updating a Ruddr invoice from Sage Intacct if the Sage line is not mapped to an Invoice Item in Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={invoiceItems.data}
                      name="defaultInvoiceItemId"
                      typeLabel="Ruddr Invoice Item"
                      value={configuration.data.defaultInvoiceItemId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Sync Method
                    <HelpTooltip
                      message="How Ruddr posts invoices, credit notes and reimbursable expenses to Sage Intacct: directly as AR/AP transactions against GL accounts, or as Order Entry and Purchasing documents based on Sage items."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SingleSelect
                      value={configuration.data.syncMethod ?? 'direct'}
                      onChange={(event) => {
                        const method = event.target.value;
                        handleValueChange('syncMethod', method);
                        if (method === 'direct') {
                          handleValueChange('orderEntryDefaultItemKey', null);
                          handleValueChange('purchasingDefaultItemKey', null);
                        }
                      }}
                      showEmptyOption={false}>
                      <option value="direct">Direct (GL accounts)</option>
                      <option value="documents">Documents (Order Entry &amp; Purchasing)</option>
                    </SingleSelect>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Delete/Unpublish Default Behavior
                    <HelpTooltip
                      message="When items are deleted or unpublished in Ruddr, this setting controls the default behavior for how the corresponding items in Sage Intacct will be handled."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SingleSelect
                      value={configuration.data.defaultRemoveFromAccounting ? 'remove' : 'keep'}
                      onChange={(event) =>
                        handleValueChange('defaultRemoveFromAccounting', event.target.value === 'remove')
                      }
                      showEmptyOption={false}>
                      <option value="keep">Keep Items in Sage Intacct</option>
                      <option value="remove">Remove Items from Sage Intacct</option>
                    </SingleSelect>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Sage Department
                    <HelpTooltip
                      message="Ruddr will use this Sage Department on transaction lines that are not mapped to a Sage Department."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={departments.data}
                      name="sageDepartmentId"
                      typeLabel="Sage Department"
                      value={configuration.data.sageDepartmentId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                {isDocuments ? (
                  <Table.BoxRow>
                    <Table.Cell>
                      Default Sage Item
                      <HelpTooltip
                        message="Ruddr will use this Sage Item when publishing an invoice or credit note to Sage Intacct if an Invoice Item in Ruddr is not mapped to a Sage Item."
                        style={{ marginLeft: '.5rem' }}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={orderEntryItems.data}
                        name="orderEntryDefaultItemKey"
                        typeLabel="Sage Item"
                        value={configuration.data.orderEntryDefaultItemKey}
                        onChange={handleChange}
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ) : (
                  <Table.BoxRow>
                    <Table.Cell>
                      Default Sage Income Account
                      <HelpTooltip
                        message="Ruddr will use this default income account when publishing an invoice to Sage Intacct if an Invoice Item in Ruddr is not mapped to a Sage Income Account."
                        style={{ marginLeft: '.5rem' }}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={items.data}
                        name="sageIncomeAccountId"
                        typeLabel="Sage Income Account"
                        value={configuration.data.sageIncomeAccountId}
                        onChange={handleChange}
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                )}

                <Table.BoxRow>
                  <Table.Cell>
                    Default Payment Method
                    <HelpTooltip
                      message="Ruddr will use this Sage Intacct payment method when posting an AR payment if the Ruddr payment method is not mapped to a Sage payment method."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={paymentMethodValues.data}
                      name="sagePaymentMethodValue"
                      typeLabel="Sage Payment Method"
                      value={configuration.data.sagePaymentMethodValue}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                {configuration.data.sageTaxCalculationMethod === 'VAT' && (
                  <>
                    <Table.BoxRow>
                      <Table.Cell>
                        Default Taxable Tax Detail
                        <HelpTooltip
                          message="Ruddr will use this tax detail when publishing an invoice to Sage Intacct if an invoice line is taxable but the invoice's tax rate is not mapped to a Sage tax detail."
                          style={{ marginLeft: '.5rem' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <OptionSelect
                          defaultOptions={taxDetails.data.filter((detail) => detail.taxType === 'sale')}
                          name="sageTaxableTaxDetailId"
                          typeLabel="Sage Tax Detail"
                          value={configuration.data.sageTaxableTaxDetailId}
                          onChange={handleChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>

                    <Table.BoxRow>
                      <Table.Cell>
                        Default Non-Taxable Tax Detail
                        <HelpTooltip
                          message="This Sage Intacct tax solution requires a tax detail on every invoice line. Ruddr will use this tax detail for non-taxable lines and tax-free invoices."
                          style={{ marginLeft: '.5rem' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <OptionSelect
                          defaultOptions={taxDetails.data.filter((detail) => detail.taxType === 'sale')}
                          name="sageNonTaxableTaxDetailId"
                          typeLabel="Sage Tax Detail"
                          value={configuration.data.sageNonTaxableTaxDetailId}
                          onChange={handleChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  </>
                )}

                <Table.BoxRow>
                  <Table.Cell>
                    Reimbursable Expenses Sync
                    <HelpTooltip
                      message="When enabled, approved reimbursable expenses will be synced to Sage Intacct as vendor bills."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SingleSelect
                      value={configuration.data.vendorBillEnabled ? 'yes' : 'no'}
                      onChange={(event) => {
                        const enabled = event.target.value === 'yes';
                        handleValueChange('vendorBillEnabled', enabled);
                        if (enabled && !configuration.data.vendorBillSyncStartDate) {
                          handleValueChange('vendorBillSyncStartDate', moment().format(dateFormats.isoDate));
                        }
                      }}
                      showEmptyOption={false}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </SingleSelect>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Reimbursable Expenses Sync Start Date
                    <HelpTooltip
                      message="Reimbursable expenses dated before this date will not be synced."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <DatePickerContainer>
                      <DayPickerInput
                        value={configuration.data.vendorBillSyncStartDate}
                        placeholder="Sync start date"
                        clearable={false}
                        onChange={(value) => handleValueChange('vendorBillSyncStartDate', value)}
                      />
                    </DatePickerContainer>
                  </Table.Cell>
                </Table.BoxRow>

                {isDocuments ? (
                  <Table.BoxRow>
                    <Table.Cell>
                      Default Purchasing Item
                      <HelpTooltip
                        message="Ruddr will use this Sage Item when syncing expenses to Sage Intacct if an expense category is not mapped to a Sage Item."
                        style={{ marginLeft: '.5rem' }}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={purchasingItems.data}
                        name="purchasingDefaultItemKey"
                        typeLabel="Sage Item"
                        value={configuration.data.purchasingDefaultItemKey}
                        onChange={handleChange}
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ) : (
                  <Table.BoxRow>
                    <Table.Cell>
                      Default Sage Expense Account
                      <HelpTooltip
                        message="Ruddr will use this Sage Account when syncing expenses to Sage Intacct if an expense category is not mapped to a Sage Account."
                        style={{ marginLeft: '.5rem' }}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={expenseAccounts.data}
                        name="sageExpenseAccountId"
                        typeLabel="Sage Account"
                        value={configuration.data.sageExpenseAccountId}
                        onChange={handleChange}
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                )}

                {configuration.data.sageTaxCalculationMethod === 'VAT' && (
                  <Table.BoxRow>
                    <Table.Cell>
                      Default Non-Taxable Purchasing Tax Detail
                      <HelpTooltip
                        message="This Sage Intacct tax solution requires a tax detail on every vendor bill line. Ruddr will use this purchasing tax detail on reimbursable expense bills, which carry no tax."
                        style={{ marginLeft: '.5rem' }}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={taxDetails.data.filter((detail) => detail.taxType === 'purchase')}
                        name="sageNonTaxablePurchasingTaxDetailId"
                        typeLabel="Sage Tax Detail"
                        value={configuration.data.sageNonTaxablePurchasingTaxDetailId}
                        onChange={handleChange}
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                )}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
